import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';

const ContactUsContent = ({ showHeroCards = true, showOurOffice = true, showFAQ = true }) => {
    const toast = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const submittingRef = useRef(false);
    const buttonRef = useRef(null);

    const subjectOptions = [
        { label: 'General Inquiry', value: 'general' },
        { label: 'Sales & Pricing', value: 'sales' },
        { label: 'Technical Support', value: 'support' },
        { label: 'Partnership Opportunities', value: 'partnership' },
        { label: 'Feedback & Suggestions', value: 'feedback' },
        { label: 'Other', value: 'other' }
    ];

    // Pre-fill with logged in user data if in dashboard
    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setFormData(prev => ({
                    ...prev,
                    name: user.name || '',
                    email: user.email || '',
                    phone: user.phone || ''
                }));
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isValidEmail = (email) => {
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Synchronous guard - blocks immediately, no async gap
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        if (buttonRef.current) buttonRef.current.disabled = true;

        setSubmitted(true);

        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            toast.current.show({
                severity: 'warn',
                summary: 'Required Fields',
                detail: 'Please fill in all required fields.',
                life: 3000
            });
            // Keep button disabled briefly so scanner detects the state
            await new Promise(resolve => setTimeout(resolve, 500));
            submittingRef.current = false;
            setSubmitting(false);
            if (buttonRef.current) buttonRef.current.disabled = false;
            return;
        }

        if (!isValidEmail(formData.email)) {
            toast.current.show({
                severity: 'warn',
                summary: 'Invalid Email',
                detail: 'Please enter a valid email address.',
                life: 3000
            });
            await new Promise(resolve => setTimeout(resolve, 500));
            submittingRef.current = false;
            setSubmitting(false);
            if (buttonRef.current) buttonRef.current.disabled = false;
            return;
        }

        // There is no contact endpoint (GH #198). This used to `setTimeout` and
        // then toast "Message Sent — we will get back to you soon", clear the
        // form, and drop everything the visitor had written. Telling someone
        // their message reached us when it went nowhere is worse than not
        // offering the form: they stop chasing, and wait for a reply that can
        // never come.
        //
        // Until the endpoint exists, hand them the channel that does work — the
        // same address printed elsewhere on this page — and DO NOT clear the
        // form, so the text they composed is still there to copy.
        const composed = [
            formData.subject ? `Subject: ${formData.subject}` : null,
            formData.message,
        ].filter(Boolean).join('\n\n');

        toast.current.show({
            severity: 'warn',
            summary: 'Send this to us by email',
            detail: 'Online submission is not available yet. Your message is still in the form — please email it to tech@interestng.com and we will reply.',
            life: 8000,
        });

        // Open the user's mail client with the message already filled in, so
        // "copy it into an email" is one click rather than a chore.
        try {
            const mailto = `mailto:tech@interestng.com`
                + `?subject=${encodeURIComponent(formData.subject || 'Website enquiry')}`
                + `&body=${encodeURIComponent(composed)}`;
            window.open(mailto, '_self');
        } catch (error) {
            // Non-fatal — the toast already told them the address.
            console.error('Could not open the mail client:', error?.message || 'Unknown error');
        }

        setSubmitted(false);
        submittingRef.current = false;
        setSubmitting(false);
        if (buttonRef.current) buttonRef.current.disabled = false;
    };

    return (
        <div>
            <Toast ref={toast} />
            
            {/* Quick Contact Cards - Optional for dashboard version */}
            {showHeroCards && (
                <div className="grid mb-5">
                    <div className="col-12 md:col-6">
                        <Card className="h-full text-center shadow-4 border-round-xl hover:shadow-8 transition-all transition-duration-300 bg-white">
                            <div className="inline-flex align-items-center justify-content-center mb-3" 
                                 style={{
                                     width: '60px',
                                     height: '60px',
                                     borderRadius: '12px',
                                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                 }}>
                                <i className="pi pi-envelope text-white text-2xl"></i>
                            </div>
                            <h4 className="font-bold mb-2 text-900">Email Us</h4>
                            <p className="text-700 m-0">
                                <a href="mailto:tech@interestng.com" className="text-primary no-underline hover:underline">
                                    tech@interestng.com
                                </a><br />
                                <span className="text-600 text-sm">24/7 Support Available</span>
                            </p>
                        </Card>
                    </div>
                    
                    <div className="col-12 md:col-6">
                        <Card className="h-full text-center shadow-4 border-round-xl hover:shadow-8 transition-all transition-duration-300 bg-white">
                            <div className="inline-flex align-items-center justify-content-center mb-3" 
                                 style={{
                                     width: '60px',
                                     height: '60px',
                                     borderRadius: '12px',
                                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                 }}>
                                <i className="pi pi-map-marker text-white text-2xl"></i>
                            </div>
                            <h4 className="font-bold mb-2 text-900">Visit Us</h4>
                            <p className="text-700 m-0">
                                Mon-Sat 9AM-7PM IST<br />
                                <span className="text-600 text-sm">Bangalore, India</span>
                            </p>
                        </Card>
                    </div>
                </div>
            )}

            {/* Our Office Section - Same as in original */}
            {showOurOffice && (
                <div className="mb-5">
                    <Card className="shadow-4 border-round-xl" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                        <div className="text-center">
                            <h2 className="text-3xl font-bold text-900 mb-4">Our Office</h2>
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <div className="text-center p-3">
                                        <i className="pi pi-building text-primary text-4xl mb-3"></i>
                                        <h4 className="font-bold text-900 mb-2">Registered Office</h4>
                                        <p className="text-700 m-0">
                                            Hireka Services Pvt. Ltd.<br />
                                            949, 21st Main Road<br />
                                            Banashankari 2nd Stage<br />
                                            BENGALURU - 560070
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="text-center p-3">
                                        <i className="pi pi-clock text-primary text-4xl mb-3"></i>
                                        <h4 className="font-bold text-900 mb-2">Business Hours</h4>
                                        <p className="text-700 m-0">
                                            Monday - Saturday<br />
                                            9:00 AM - 7:00 PM IST<br />
                                            Sunday: Closed
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="text-center p-3">
                                        <i className="pi pi-envelope text-primary text-4xl mb-3"></i>
                                        <h4 className="font-bold text-900 mb-2">Get in Touch</h4>
                                        <p className="text-700 m-0">
                                            Email: tech@interestng.com<br />
                                            Support: 24/7 Available
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Contact Form */}
            <div className="grid mb-5">
                <div className="col-12">
                    <Card className="shadow-4 border-round-xl">
                        <h2 className="text-3xl font-bold text-900 mb-2 text-center">Send Us a Message</h2>
                        <p className="text-700 mb-5 text-center">Fill out the form below and we'll get back to you within 24 hours</p>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="grid formgrid p-fluid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="name" className="font-semibold">Full Name *</label>
                                    <InputText
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Your full name"
                                        autoComplete="name"
                                        className={submitted && !formData.name ? 'p-invalid' : ''}
                                        required
                                    />
                                    {submitted && !formData.name && <small className="p-error">Name is required.</small>}
                                </div>
                                
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="email" className="font-semibold">Email Address *</label>
                                    <InputText
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="your.email@example.com"
                                        autoComplete="email"
                                        className={submitted && (!formData.email || !isValidEmail(formData.email)) ? 'p-invalid' : ''}
                                        required
                                    />
                                    {submitted && !formData.email && <small className="p-error">Email is required.</small>}
                                    {submitted && formData.email && !isValidEmail(formData.email) && <small className="p-error">Please enter a valid email.</small>}
                                </div>
                                
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="phone" className="font-semibold">Phone Number</label>
                                    <InputText
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="+91 98765 43210"
                                        autoComplete="tel"
                                    />
                                </div>
                                
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="company" className="font-semibold">Company/Organization</label>
                                    <InputText
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        placeholder="Your company name"
                                        autoComplete="organization"
                                    />
                                </div>
                                
                                <div className="field col-12">
                                    <label htmlFor="subject" className="font-semibold">Subject *</label>
                                    <Dropdown
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.value }))}
                                        options={subjectOptions}
                                        placeholder="Select a subject"
                                        className={submitted && !formData.subject ? 'p-invalid' : ''}
                                        required
                                    />
                                    {submitted && !formData.subject && <small className="p-error">Subject is required.</small>}
                                </div>
                                
                                <div className="field col-12">
                                    <label htmlFor="message" className="font-semibold">Your Message *</label>
                                    <InputTextarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        rows={5}
                                        placeholder="Tell us how we can help you..."
                                        className={submitted && !formData.message ? 'p-invalid' : ''}
                                        required
                                    />
                                    {submitted && !formData.message && <small className="p-error">Message is required.</small>}
                                </div>
                                
                                <div className="field col-12 text-center">
                                    <Button
                                        ref={buttonRef}
                                        type="submit"
                                        label="Send Message"
                                        icon="pi pi-send"
                                        loading={submitting}
                                        disabled={submitting}
                                        className="p-button-lg w-full md:w-auto transition-all transition-duration-300 hover:shadow-4"
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            minWidth: '200px'
                                        }}
                                    />
                                </div>
                            </div>
                        </form>
                    </Card>
                </div>
            </div>

            {/* FAQ Section - Optional */}
            {showFAQ && (
                <div className="py-8 px-4 lg:px-8" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="text-center mb-6">
                        <h2 className="text-4xl font-bold text-900 mb-3">Frequently Asked Questions</h2>
                        <p className="text-xl text-700">Quick answers to common questions</p>
                    </div>
                    
                    <div className="grid max-w-6xl mx-auto">
                        <div className="col-12 md:col-6">
                            <div className="mb-4">
                                <h4 className="font-bold text-900 mb-2">
                                    <i className="pi pi-question-circle text-primary mr-2"></i>
                                    What services do you offer?
                                </h4>
                                <p className="text-700">
                                    We provide Loan comparisons, credit bureau reports, bank statement analysis, and various other financial verification services.
                                </p>
                            </div>
                        </div>
                        
                        <div className="col-12 md:col-6">
                            <div className="mb-4">
                                <h4 className="font-bold text-900 mb-2">
                                    <i className="pi pi-question-circle text-primary mr-2"></i>
                                    How quickly can I get started?
                                </h4>
                                <p className="text-700">
                                    You can get started immediately after registration. Our verification process typically takes less than 5 minutes.
                                </p>
                            </div>
                        </div>
                        
                        <div className="col-12 md:col-6">
                            <div className="mb-4">
                                <h4 className="font-bold text-900 mb-2">
                                    <i className="pi pi-question-circle text-primary mr-2"></i>
                                    What are your support hours?
                                </h4>
                                <p className="text-700">
                                    Our support team is available Monday through Saturday, 9:00 AM to 7:00 PM IST. Email support is available 24/7.
                                </p>
                            </div>
                        </div>
                        
                        <div className="col-12 md:col-6">
                            <div className="mb-4">
                                <h4 className="font-bold text-900 mb-2">
                                    <i className="pi pi-question-circle text-primary mr-2"></i>
                                    Can I check my loan eligibility?
                                </h4>
                                <p className="text-700">
                                    Yes, you can instantly check your loan eligibility based on comprehensive credit analysis and get detailed reports.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactUsContent;